import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { TableToolbar, matches, exportRows, Empty } from "./shared";

export const ACTION_LABELS = {
  investment_active: "Investment approved",
  investment_rejected: "Investment rejected",
  investment_completed: "Payout recorded",
  investment_pending: "Investment set to pending",
  kyc_verified: "KYC verified",
  kyc_rejected: "KYC rejected",
  kyc_pending: "KYC set to pending",
  plan_created: "Plan created",
  plan_updated: "Plan updated",
  plan_deleted: "Plan deleted",
  payment_settings_updated: "Payment details changed",
  whatsapp_settings_updated: "WhatsApp/SMS settings changed",
  referral_settings_updated: "Referral rule changed",
  referral_bonus_created: "Referral bonus pending",
  referral_bonus_credited: "Referral bonus credited",
  leaderboard_settings_updated: "Leaderboard settings changed",
  password_changed: "Admin password changed",
  support_reply: "Support reply sent",
  reminders_run: "Reminders run manually",
};

const TONE = {
  investment_active: "text-emerald-400 border-emerald-400/30",
  investment_completed: "text-[#D4AF37] border-[#D4AF37]/30",
  referral_bonus_credited: "text-[#D4AF37] border-[#D4AF37]/30",
  kyc_verified: "text-emerald-400 border-emerald-400/30",
  investment_rejected: "text-red-400 border-red-400/30",
  kyc_rejected: "text-red-400 border-red-400/30",
  plan_deleted: "text-red-400 border-red-400/30",
};

const detailText = (d) => Object.entries(d || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}: ${typeof v === "number" ? v.toLocaleString("en-IN") : v}`).join(" · ");

const COLUMNS = [
  { label: "When", value: (l) => fmtDate(l.created_at) },
  { label: "Admin", value: "admin_email" },
  { label: "Action", value: (l) => ACTION_LABELS[l.action] || l.action },
  { label: "Target Type", value: "target_type" },
  { label: "Target", value: "target_label" },
  { label: "Target ID", value: "target_id" },
  { label: "Details", value: (l) => detailText(l.details) },
];

export const ActivityLogTab = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  useEffect(() => {
    api.get("/admin/logs").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  const present = [...new Set(items.map((i) => i.action))];
  const rows = items
    .filter((i) => action === "all" || i.action === action)
    .filter((i) => matches({ ...i, label: ACTION_LABELS[i.action] || i.action, details_text: detailText(i.details) }, q));
  return (
    <div data-testid="admin-activity-tab">
      <TableToolbar title="Activity Log" count={rows.length} query={q} onQuery={setQ} placeholder="Search admin, investor, plan…" testId="activity" onExport={() => exportRows("activity-log", COLUMNS, rows)}>
        <select data-testid="activity-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-xs text-white gold-focus">
          <option value="all">All actions</option>
          {present.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
      </TableToolbar>
      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-accent text-[10px] tracking-[0.2em] uppercase text-white/35">
          <span className="col-span-2">When</span>
          <span className="col-span-2">Admin</span>
          <span className="col-span-3">Action</span>
          <span className="col-span-5">Target & details</span>
        </div>
        {rows.map((l) => (
          <div key={l.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 last:border-0 items-start" data-testid={`activity-row-${l.id}`}>
            <span className="col-span-2 text-xs text-white/50">{fmtDate(l.created_at)}</span>
            <span className="col-span-2 text-xs text-white/70 break-all">{l.admin_email}</span>
            <div className="col-span-3">
              <span className={`inline-block border px-2 py-1 font-accent text-[9px] tracking-[0.15em] uppercase ${TONE[l.action] || "text-white/60 border-white/15"}`} data-testid={`activity-action-${l.id}`}>
                {ACTION_LABELS[l.action] || l.action}
              </span>
            </div>
            <div className="col-span-5">
              <p className="text-sm text-white" data-testid={`activity-target-${l.id}`}>{l.target_label || "—"}</p>
              {detailText(l.details) && <p className="text-xs text-white/40 mt-1 leading-relaxed">{detailText(l.details)}</p>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty testId="activity-empty" text={q || action !== "all" ? "No activity matches your filter." : "No admin actions recorded yet. Approvals, payouts, KYC decisions and settings changes will appear here."} />}
      </div>
    </div>
  );
};
